import React, { FunctionComponent, useState } from 'react';
import { KeyIcon, RefreshIcon, Spinner, TrashIcon } from 'evergreen-ui';
import styles from './RecruiteeCard.module.css';
import AppButton from 'components/AppButton/AppButton';
import DialogComponent from 'components/Dialog/Dialog';
import ProgressSpinner from 'components/ProgressSpinner/ProgressSpinner';
import { formatDate, ISODateToString } from 'utils/time';
import { getApiErrorMessage } from 'utils/apiError';
import {
    useGenerateRecruiteeKeyMutation,
    useGetRecruiteeIntegrationQuery,
    useRevokeRecruiteeKeyMutation,
} from '../../../store/api/integrations';
import RecruiteeKeyDialog from './RecruiteeKeyDialog';

interface RecruiteeCardProps {
    companyIdentifier: string;
}

/**
 * Tellent Recruitee connection of a company: the status of its key, and the
 * actions to generate, regenerate or revoke it. A company has at most one
 * key, so regenerating revokes the old one on the backend.
 */
const RecruiteeCard: FunctionComponent<RecruiteeCardProps> = (props) => {
    const { companyIdentifier } = props;

    const {
        data: integration,
        isLoading,
        isError,
    } = useGetRecruiteeIntegrationQuery(companyIdentifier);

    const [
        generateKey,
        {
            data: generatedKey,
            isLoading: isGenerating,
            error: generateError,
            reset: resetGenerate,
        },
    ] = useGenerateRecruiteeKeyMutation();

    const [
        revokeKey,
        { isLoading: isRevoking, error: revokeError, reset: resetRevoke },
    ] = useRevokeRecruiteeKeyMutation();

    const [confirmAction, setConfirmAction] = useState<
        'regenerate' | 'revoke' | null
    >(null);

    const isConnected = !!integration?.connected;
    const isBusy = isGenerating || isRevoking;
    const actionError = generateError || revokeError;

    const onGenerate = () => {
        setConfirmAction(null);
        resetRevoke();
        generateKey(companyIdentifier);
    };

    const onRevoke = () => {
        setConfirmAction(null);
        resetGenerate();
        revokeKey(companyIdentifier);
    };

    // The key is only returned once. Resetting the mutation drops it from the
    // store as soon as the admin closes the dialog.
    const onKeyDialogClose = () => {
        resetGenerate();
    };

    const renderDetails = () => {
        if (!integration || !isConnected) {
            return null;
        }
        return (
            <table className={styles.recruitee_card__details}>
                <tbody>
                    <tr>
                        <td>Key</td>
                        <td>
                            <code>{`nlrt_…${integration.keyHint ?? ''}`}</code>
                        </td>
                    </tr>
                    <tr>
                        <td>Created</td>
                        <td>
                            {integration.createdAt
                                ? formatDate(integration.createdAt)
                                : '-'}
                        </td>
                    </tr>
                    <tr>
                        <td>Created by</td>
                        <td>
                            {integration.createdBy
                                ? integration.createdBy.name ||
                                  integration.createdBy.id
                                : '-'}
                        </td>
                    </tr>
                    <tr>
                        <td>Last used</td>
                        <td>
                            {integration.lastUsedAt
                                ? ISODateToString(integration.lastUsedAt)
                                : 'Not used yet'}
                        </td>
                    </tr>
                    <tr>
                        <td>Recruitee company IDs</td>
                        <td>
                            {integration.recruiteeCompanyIds.length
                                ? integration.recruiteeCompanyIds.join(', ')
                                : 'None yet'}
                        </td>
                    </tr>
                </tbody>
            </table>
        );
    };

    const renderActions = () => {
        if (!integration) {
            return null;
        }
        return (
            <div className={styles.recruitee_card__actions}>
                {isBusy && <Spinner size={20} />}
                {!isConnected && (
                    <AppButton
                        icon={KeyIcon}
                        onClick={onGenerate}
                        isDisabled={isBusy}>
                        Generate key
                    </AppButton>
                )}
                {isConnected && (
                    <>
                        <AppButton
                            icon={RefreshIcon}
                            onClick={() => setConfirmAction('regenerate')}
                            isDisabled={isBusy}>
                            Regenerate key
                        </AppButton>
                        <AppButton
                            icon={TrashIcon}
                            color="red"
                            onClick={() => setConfirmAction('revoke')}
                            isDisabled={isBusy}>
                            Revoke key
                        </AppButton>
                    </>
                )}
            </div>
        );
    };

    return (
        <div className={styles.recruitee_card}>
            <div className={styles.recruitee_card__header}>
                <h2>Tellent Recruitee</h2>
                {integration && (
                    <span
                        className={
                            isConnected
                                ? styles.recruitee_card__status_connected
                                : styles.recruitee_card__status_not_connected
                        }>
                        {isConnected ? 'Connected' : 'Not connected'}
                    </span>
                )}
            </div>
            <p className={styles.recruitee_card__description}>
                Let recruiters send Neurolytics scans to candidates from Tellent
                Recruitee, and see the results on the candidate's profile.
                Recruitee connects to Neurolytics with a key that you generate
                here.
            </p>

            {isLoading && <ProgressSpinner />}
            {isError && (
                <p className={styles.recruitee_card__error}>
                    Could not load the Recruitee integration. Please try again
                    later.
                </p>
            )}

            {renderDetails()}

            {!!actionError && (
                <p className={styles.recruitee_card__error}>
                    {getApiErrorMessage(actionError)}
                </p>
            )}

            {renderActions()}

            <DialogComponent
                title="Regenerate the Recruitee key?"
                confirmButtonText="Regenerate key"
                confirmIcon={RefreshIcon}
                isShown={confirmAction === 'regenerate'}
                onClose={() => setConfirmAction(null)}
                onConfirm={onGenerate}>
                <div className={styles.dialog__content}>
                    <p>
                        The current key stops working right away. Until you
                        paste the new key into Recruitee (open the Neurolytics
                        integration and click <b>Change</b>), recruiters can't
                        send Neurolytics scans or download reports from
                        Recruitee.
                    </p>
                </div>
            </DialogComponent>

            <DialogComponent
                title="Revoke the Recruitee key?"
                confirmButtonText="Revoke key"
                confirmIcon={TrashIcon}
                buttonColor="red"
                isShown={confirmAction === 'revoke'}
                onClose={() => setConfirmAction(null)}
                onConfirm={onRevoke}>
                <div className={styles.dialog__content}>
                    <p>
                        The key stops working right away. Recruiters can't send
                        Neurolytics scans or download reports from Recruitee
                        until a new key is generated here and the integration is
                        set up again in Recruitee.
                    </p>
                    <p>
                        To disconnect completely, also remove the Neurolytics
                        integration in Recruitee.
                    </p>
                </div>
            </DialogComponent>

            <RecruiteeKeyDialog
                recruiteeKey={generatedKey?.key}
                onClose={onKeyDialogClose}
            />
        </div>
    );
};

export default RecruiteeCard;
